import React from 'react';
import { Box, InputLabel, Typography, FormHelperText } from '@mui/material';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';

export interface InputLabelTextProps {
  label?: string | null;
  name: string;
  prefix?: string;
  hideLabel?: boolean;
  children: React.ReactNode;
  required?: boolean | null;
  errorMessage?: string;
  showModified?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  changeRequested?: boolean;
  floatingElement?: React.ReactNode;
  hideErrorMsg?: boolean;
  color?: string;
}
const InputLabelText = ({
  label,
  name,
  prefix,
  hideLabel = false,
  children,
  required = true,
  errorMessage,
  showModified = false,
  helperText,
  disabled,
  changeRequested = false,
  floatingElement,
  hideErrorMsg = false,
  color,
}: InputLabelTextProps) => {
  const getColor = () => {
    if (errorMessage) return 'error.main';
    if (disabled) return 'text.disabled';
    return 'primary.main';
  };

  const footer = Boolean(changeRequested || helperText || (errorMessage && !hideErrorMsg));

  const getHelperTextColor = () => {
    if (errorMessage) return 'error.main';
    if (disabled) return 'text.disabled';
    return 'text.secondary';
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns="min-content auto"
      gridTemplateAreas={[`${hideLabel ? '' : '". label"'} "prefix input" ${footer ? '". footer"' : ''}`]}
      width="100%"
      rowGap="8px"
      alignItems="center"
    >
      {prefix && <Typography sx={{ gridArea: 'prefix', mr: '5px' }}>{prefix}</Typography>}
      {!hideLabel && (
        <Box display="flex" justifyContent="space-between" gridArea="label">
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <InputLabel
              id={`${name}-label`}
              sx={{ mr: 1, color: getColor(), fontWeight: 700 }}
              htmlFor={`${name}-input`}
            >
              <Typography variant="subtitle2" component="span" color={color || 'inherit'}>
                {label}
              </Typography>
            </InputLabel>
            {!required && !hideErrorMsg && (
              <InputLabel sx={{ color: 'text.secondary' }} htmlFor={`${name}-input`}>
                <Typography variant="body2" component="span" lineHeight="normal">
                  Optional
                </Typography>
              </InputLabel>
            )}
          </Box>
          {showModified && (
            <Typography
              variant="body2"
              color="success.main"
              component="div"
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                gap: '4px',
              }}
            >
              <UpdateOutlinedIcon fontSize="inherit" />
              <span>Modified</span>
            </Typography>
          )}
        </Box>
      )}
      <Box gridArea="input" position="relative">
        {children}
        {floatingElement}
      </Box>
      {footer && (
        <Box gridArea="footer" display="flex" flexDirection="column" gap={0.375}>
          {changeRequested && (
            <FormHelperText
              sx={{ color: (theme) => theme.palette.changeRequested.main, m: 0 }}
              data-testid={`${label}-change`}
            >
              Please change this field
            </FormHelperText>
          )}
          {helperText && (
            <FormHelperText
              sx={{
                color: getHelperTextColor(),
                m: 0,
              }}
            >
              {helperText}
            </FormHelperText>
          )}
          {errorMessage && !hideErrorMsg && (
            <FormHelperText error={!!errorMessage} data-testid={`${label}-error`} sx={{ gridArea: 'error', m: 0 }}>
              {errorMessage}
            </FormHelperText>
          )}
        </Box>
      )}
    </Box>
  );
};

export default InputLabelText;
